/**
 * The weekly goal a student is measured against: newly demonstrating this many skills in the week
 * means the goal is met. Fixed for now, but read from here rather than hardcoded at each use so the
 * counter, the chart's goal marker and the drill-down can never disagree.
 */
export const WEEKLY_PACE_GOAL = 2;

/**
 * The highest category, which is open ended. Mirrors `WeeklyPaceCategories.TOP_CATEGORY` in the
 * GraphQL service: the resolver already collapses anything at or above it into this bucket.
 */
export const WEEKLY_PACE_TOP_CATEGORY = 4;

export interface WeeklyPaceColumn {
  /** 0 to WEEKLY_PACE_TOP_CATEGORY, where the top one is open ended. */
  category: number;
  label: string;
  /** Unit word rendered under the label, only on the columns the design shows it on. */
  unit?: string;
  students: number;
  /** True on the merged zero column when students who attempted nothing landed in it. */
  includesNoAttempts: boolean;
  meetsGoal: boolean;
}

export interface WeeklyPaceSummary {
  /** Only the categories at least one student falls into, ordered from 0 to the top category. */
  columns: WeeklyPaceColumn[];
  studentsMetGoal: number;
  totalStudents: number;
  /** False when nobody attempted a Skill Challenge, which is what the empty state keys off. */
  hasAnyActivity: boolean;
}

interface WeeklySnapshotBucket {
  readonly uniqueSkillsPassed: number | null | undefined;
  readonly numberOfStudents: number;
}

// Reproduces the design's labelling: the unit word only appears on the two columns below the goal.
const COLUMN_DEFINITIONS: ReadonlyArray<{ category: number; label: string; unit?: string }> = [
  { category: 0, label: '0', unit: 'skill' },
  { category: 1, label: '1', unit: 'skill' },
  { category: 2, label: '2' },
  { category: 3, label: '3' },
  { category: WEEKLY_PACE_TOP_CATEGORY, label: `${WEEKLY_PACE_TOP_CATEGORY}+` },
];

/**
 * Turns the weekly snapshot report into the columns the Pace chart draws, plus the goal counter
 * figures. Both come from the same report so a bar segment's size can never contradict the counter.
 *
 * Students who attempted no Skill Challenge at all arrive in their own bucket (a null
 * `uniqueSkillsPassed`) and are merged into the zero column, which therefore reads as "no new skills
 * this week" regardless of whether anything was attempted.
 */
export const buildWeeklyPaceSummary = (
  report: ReadonlyArray<WeeklySnapshotBucket> | null | undefined,
): WeeklyPaceSummary => {
  const studentsByCategory = new Map<number, number>();
  let noAttemptStudents = 0;
  let studentsMetGoal = 0;
  let totalStudents = 0;

  for (const bucket of report ?? []) {
    const students = bucket.numberOfStudents;
    totalStudents += students;

    if (bucket.uniqueSkillsPassed === null || bucket.uniqueSkillsPassed === undefined) {
      noAttemptStudents += students;
      continue;
    }

    // Defensive: the resolver already collapses the open-ended top category.
    const category = Math.min(bucket.uniqueSkillsPassed, WEEKLY_PACE_TOP_CATEGORY);
    studentsByCategory.set(category, (studentsByCategory.get(category) ?? 0) + students);

    if (category >= WEEKLY_PACE_GOAL) studentsMetGoal += students;
  }

  studentsByCategory.set(0, (studentsByCategory.get(0) ?? 0) + noAttemptStudents);

  const columns = COLUMN_DEFINITIONS.map(definition => ({
    ...definition,
    students: studentsByCategory.get(definition.category) ?? 0,
    includesNoAttempts: definition.category === 0 && noAttemptStudents > 0,
    meetsGoal: definition.category >= WEEKLY_PACE_GOAL,
  })).filter(column => column.students > 0);

  return {
    columns,
    studentsMetGoal,
    totalStudents,
    hasAnyActivity: totalStudents > noAttemptStudents,
  };
};
